import React, { useEffect, useState, useCallback } from "react";
import { MuiMaterials } from '../@index.js';
import * as global from '../data/global';
import { createApiEndpoint } from '../index.js';
import { ModalChrome, ModalBody, ModalContentTile, SiteSelectPhase, Spinner, SITE_WINDOW_IDS, CenteredLoadingOverlay, withScrollHintMenuProps } from './windowChrome/index.js';

const getThemeColor = (lightColor, darkColor) => {
    if (global.gw.theme === 'light') {
        return lightColor;
    }
    return darkColor;
};

const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
        const d = new Date(dateStr);
        return d.toLocaleString("en-us", global.dateFormat || { month: '2-digit', day: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' }).replace(", ", " ");
    } catch {
        return String(dateStr);
    }
};

const parseDecompressed = async (res, key) => {
    if (!res || typeof res !== 'object') return null;
    const raw = res?.data?.[key] ?? res?.[key];
    if (!raw || typeof raw !== 'string') return null;
    try {
        const ab = global.base64StringToArrayBuffer(raw);
        const decompressed = await global.decompress(ab, "gzip");
        return decompressed ? JSON.parse(decompressed) : null;
    } catch {
        return null;
    }
};

const SiteGradesModal = ({ isModalOpen, setIsModalOpen, initialSiteGuid = null }) => {
    const [sites, setSites] = useState([]);
    const [sitesLoading, setSitesLoading] = useState(false);
    const [pickerSiteGuid, setPickerSiteGuid] = useState('');
    const [selectedSite, setSelectedSite] = useState('');
    const [loading, setLoading] = useState(false);
    const [dates, setDates] = useState([]);
    const [dateDataMap, setDateDataMap] = useState({});
    const [expanded, setExpanded] = useState([]);
    const [viewTab, setViewTab] = useState(0);
    const [compareDateA, setCompareDateA] = useState('');
    const [compareDateB, setCompareDateB] = useState('');
    const [filterName, setFilterName] = useState('');
    const [sortBy, setSortBy] = useState(null); // 'A' | 'B' | 'delta'
    const [sortDir, setSortDir] = useState('asc'); // 'asc' | 'desc'
    const [treeSortDateDir, setTreeSortDateDir] = useState('desc'); // date order (top-level)
    const [treeSortGradeDir, setTreeSortGradeDir] = useState('desc'); // grade order for bins/cables/sensors within each date
    const [treeSortNameDir, setTreeSortNameDir] = useState('asc'); // name order for bins/cables/sensors within each date
    const [treeSortInnerBy, setTreeSortInnerBy] = useState('grade'); // 'grade' | 'name' — which sort applies to items within each date
    const [trendsMetric, setTrendsMetric] = useState('Grade'); // 'Grade' | 'TimeToRead' | 'Resistance' | 'AverageFluctuation'
    const [siteLayout, setSiteLayout] = useState(null); // { bins: [...] } with cableList, sensorList
    const [currentTheme, setCurrentTheme] = useState(global.gw.theme || 'dark');
    const [forceUpdate, setForceUpdate] = useState(0);

    useEffect(() => {
        const handleThemeChange = () => {
            setCurrentTheme(global.gw.theme || 'dark');
            setForceUpdate(prev => prev + 1);
        };
        window.addEventListener('themeChanged', handleThemeChange);
        return () => window.removeEventListener('themeChanged', handleThemeChange);
    }, []);

    useEffect(() => {
        const interval = setInterval(() => {
            const currentThemeValue = global.gw.theme || 'dark';
            if (currentThemeValue !== currentTheme) {
                setCurrentTheme(currentThemeValue);
                setForceUpdate(prev => prev + 1);
            }
        }, 100);
        return () => clearInterval(interval);
    }, [currentTheme]);

    // When modal opens, fetch which sites have grades and filter dropdown to only those
    useEffect(() => {
        if (!isModalOpen[0] || !global.gw.auth) return;
        const allSites = [...(global.gw.siteInfos || [])].filter(s => s.siteGuid && !s.isHms);
        if (allSites.length === 0) {
            setSites([]);
            setSelectedSite('');
            setPickerSiteGuid('');
            return;
        }
        let cancelled = false;
        setSitesLoading(true);
        setSites([]);
        // Keep a carried site during the grades probe so tool-switching does not flash the picker.
        if (initialSiteGuid) {
            setSelectedSite(initialSiteGuid);
            setPickerSiteGuid(initialSiteGuid);
        } else {
            setSelectedSite('');
            setPickerSiteGuid('');
        }
        Promise.all(allSites.map(async (site) => {
            try {
                const res = await createApiEndpoint().fetchSiteGradeDates("direct", site.siteGuid);
                if (!res?.data?.getSiteGradeDates) return { site, hasGrades: false };
                const data = await parseDecompressed(res, 'getSiteGradeDates');
                const items = Array.isArray(data) ? data : [];
                const hasGrades = items.length > 0;
                return { site, hasGrades };
            } catch {
                return { site, hasGrades: false };
            }
        })).then((results) => {
            if (cancelled) return;
            const tempSites = results.filter(r => r.hasGrades).map(r => r.site);
            tempSites.sort((a, b) => ((a.siteName || '').toLowerCase()).localeCompare((b.siteName || '').toLowerCase()));
            setSites(tempSites);
            // Stay on the picker until the user continues — do not auto-select the first site.
            // Title-nav can seed a site that still has grades.
            if (initialSiteGuid && tempSites.some((s) => s.siteGuid === initialSiteGuid)) {
                setSelectedSite(initialSiteGuid);
                setPickerSiteGuid(initialSiteGuid);
            } else {
                setSelectedSite('');
            }
        }).finally(() => {
            if (!cancelled) setSitesLoading(false);
        });
        return () => { cancelled = true; };
    }, [isModalOpen, global.gw.siteInfos, global.gw.auth, initialSiteGuid]);

    useEffect(() => {
        if (!selectedSite) {
            setSiteLayout(null);
            return;
        }
        if (global.gw.selectedSite?.siteGuid === selectedSite && global.gw.selectedSite?.bins?.length > 0) {
            setSiteLayout({ bins: global.gw.selectedSite.bins });
            return;
        }
        let cancelled = false;
        createApiEndpoint().fetchSiteLayout("direct", selectedSite).then((layout) => {
            if (!cancelled && layout?.bins) setSiteLayout(layout);
            else if (!cancelled) setSiteLayout(null);
        }).catch(() => { if (!cancelled) setSiteLayout(null); });
        return () => { cancelled = true; };
    }, [selectedSite]);

    const loadDates = useCallback(async (siteGuid) => {
        if (!siteGuid || !global.gw.auth) return;
        setLoading(true);
        setDates([]);
        setDateDataMap({});
        try {
            const res = await createApiEndpoint().fetchSiteGradeDates("direct", siteGuid);
            if (res?.data?.getSiteGradeDates) {
                const data = await parseDecompressed(res, 'getSiteGradeDates');
                // Data is array of { dateRead, grade } or legacy array of date strings
                const items = Array.isArray(data) ? data : [];
                setDates(items.map(item => {
                    if (item && typeof item === 'object' && item.dateRead != null) {
                        return { dateStr: item.dateRead, grade: item.grade };
                    }
                    return { dateStr: String(item), grade: null };
                }));
            }
        } catch (err) {
            console.error('Error loading site grade dates:', err);
        } finally {
            setLoading(false);
        }
    }, []);

    const loadDateData = useCallback(async (siteGuid, dateStr) => {
        if (!siteGuid || !dateStr) return;
        if (dateDataMap[dateStr]?.loading || (dateDataMap[dateStr] && Array.isArray(dateDataMap[dateStr].bins))) return;
        setDateDataMap(prev => {
            if (prev[dateStr]?.loading || (prev[dateStr] && Array.isArray(prev[dateStr].bins))) return prev;
            return { ...prev, [dateStr]: { ...prev[dateStr], loading: true } };
        });
        try {
            // Extract YYYY-MM-DD to avoid timezone issues (API expects date string)
            const dateParam = (typeof dateStr === 'string' && dateStr.includes('T'))
                ? dateStr.split('T')[0]
                : (typeof dateStr === 'string' ? dateStr : new Date(dateStr).toISOString().split('T')[0]);
            const jsonWithDate = { date: dateParam };
            const jsonNoDate = { date: null };

            const fetchGrades = async (json) => {
                const [binRes, cableRes, sensorRes] = await Promise.all([
                    createApiEndpoint().fetchBinGrades("direct", json, siteGuid),
                    createApiEndpoint().fetchCableGrades("direct", json, siteGuid),
                    createApiEndpoint().fetchSensorGrades("direct", json, siteGuid),
                ]);
                const bins = [];
                const binData = await parseDecompressed(binRes, 'getBinGrades');
                if (Array.isArray(binData)) {
                    binData.forEach(bg => {
                        bins.push({
                            binId: typeof bg.BinId === 'number' ? bg.BinId : parseInt(bg.BinId, 10),
                            grade: bg.Grade,
                        });
                    });
                }
                const cables = [];
                const cableData = await parseDecompressed(cableRes, 'getCableGrades');
                if (Array.isArray(cableData)) {
                    cableData.forEach(cg => {
                        cables.push({
                            binId: typeof cg.BinId === 'number' ? cg.BinId : parseInt(cg.BinId, 10),
                            cableId: typeof cg.CableId === 'number' ? cg.CableId : parseInt(cg.CableId, 10),
                            grade: cg.Grade,
                            cableName: cg.CableName ?? cg.cableName,
                        });
                    });
                }
                const sensors = [];
                const sensorData = await parseDecompressed(sensorRes, 'getSensorGrades');
                if (Array.isArray(sensorData)) {
                    sensorData.forEach(sg => {
                        sensors.push({
                            binId: typeof sg.BinId === 'number' ? sg.BinId : parseInt(sg.BinId, 10),
                            cableId: typeof sg.CableId === 'number' ? sg.CableId : parseInt(sg.CableId, 10),
                            sensorId: typeof sg.SensorId === 'number' ? sg.SensorId : parseInt(sg.SensorId, 10),
                            grade: sg.Grade,
                            errorType: sg.ErrorType ?? sg.errorType,
                            finalValueString: sg.FinalValueString ?? sg.finalValueString,
                            finalValue: sg.FinalValue ?? sg.finalValue,
                            timeToRead: sg.TimeToRead ?? sg.timeToRead,
                            stabilizedAtReading: sg.StabilizedAtReading ?? sg.stabilizedAtReading,
                            averageFluctuation: sg.AverageFluctuation ?? sg.averageFluctuation,
                            invalidReadingsCount: sg.InvalidReadingsCount ?? sg.invalidReadingsCount,
                            stabilizationCount: sg.StabilizationCount ?? sg.stabilizationCount,
                            resistance: sg.Resistance ?? sg.resistance,
                            cableName: sg.CableName ?? sg.cableName,
                            sensorName: sg.SensorName ?? sg.sensorName,
                        });
                    });
                }
                return { bins, cables, sensors };
            };

            let { bins, cables, sensors } = await fetchGrades(jsonWithDate);
            // Fallback: if date filter returns empty, try latest (no date) - backend may have data under different date
            if (bins.length === 0 && cables.length === 0 && sensors.length === 0) {
                const fallback = await fetchGrades(jsonNoDate);
                bins = fallback.bins;
                cables = fallback.cables;
                sensors = fallback.sensors;
            }

            setDateDataMap(prev => ({
                ...prev,
                [dateStr]: { bins, cables, sensors, loading: false },
            }));
        } catch (err) {
            console.error('Error loading date grades:', err);
            setDateDataMap(prev => ({
                ...prev,
                [dateStr]: { bins: [], cables: [], sensors: [], loading: false },
            }));
        }
    }, [dateDataMap]);

    useEffect(() => {
        if (isModalOpen[0] && selectedSite) {
            loadDates(selectedSite);
        }
    }, [isModalOpen, selectedSite, loadDates]);

    useEffect(() => {
        if (viewTab === 1 && selectedSite && compareDateA && compareDateB) {
            [compareDateA, compareDateB].forEach(dateStr => {
                if (!dateDataMap[dateStr]?.bins && !dateDataMap[dateStr]?.loading) {
                    loadDateData(selectedSite, dateStr);
                }
            });
        }
    }, [viewTab, selectedSite, compareDateA, compareDateB, dateDataMap, loadDateData]);

    // When filter by name is active, load all dates' data so we can filter across dates
    useEffect(() => {
        const q = (filterName || '').trim();
        if (q && selectedSite && viewTab === 0) {
            dates.forEach(item => {
                const dateStr = item?.dateStr ?? item;
                if (dateStr && !dateDataMap[dateStr]?.bins && !dateDataMap[dateStr]?.loading) {
                    loadDateData(selectedSite, dateStr);
                }
            });
        }
    }, [filterName, selectedSite, viewTab, dates, dateDataMap, loadDateData]);

    // When on Trends tab, load all dates' data
    useEffect(() => {
        if (selectedSite && viewTab === 2) {
            dates.forEach(item => {
                const dateStr = item?.dateStr ?? item;
                if (dateStr && !dateDataMap[dateStr]?.bins && !dateDataMap[dateStr]?.loading) {
                    loadDateData(selectedSite, dateStr);
                }
            });
        }
    }, [selectedSite, viewTab, dates, dateDataMap, loadDateData]);

    const handleExpandedChange = useCallback((event, nodeIds) => {
        setExpanded(nodeIds);
        nodeIds.forEach(nodeId => {
            const dateMatch = nodeId.match(/^date-(.+)$/);
            if (dateMatch && selectedSite) {
                const dateStr = dateMatch[1];
                if (!dateDataMap[dateStr]?.bins && !dateDataMap[dateStr]?.loading) {
                    loadDateData(selectedSite, dateStr);
                }
            }
        });
    }, [selectedSite, dateDataMap, loadDateData]);

    const getBinName = (binId) => {
        const bins = siteLayout?.bins ?? sites.find(s => s.siteGuid === selectedSite)?.bins;
        if (!bins?.length) return `Bin ${binId}`;
        const bin = bins.find(b => (b.binId === binId || b.binDataId === binId || b.number === binId));
        return bin?.binName || bin?.name || `Bin ${binId}`;
    };

    const getCableName = (binId, cableId, cableNameFromApi) => {
        if (cableNameFromApi) return cableNameFromApi;
        const bins = siteLayout?.bins ?? sites.find(s => s.siteGuid === selectedSite)?.bins;
        if (!bins?.length) return `Cable ${cableId}`;
        const bin = bins.find(b => (b.binId === binId || b.binDataId === binId || b.number === binId));
        const cableList = bin?.cableList ?? bin?.CableList;
        const cable = cableList?.find(c => (c.id === cableId || c.cableId === cableId || c.CableId === cableId));
        return cable?.name ?? cable?.cableName ?? cable?.Name ?? (bin ? `${getBinName(binId)} - Cable ${cableId}` : `Cable ${cableId}`);
    };

    const getSensorName = (binId, cableId, sensorId, sensorNameFromApi) => {
        if (sensorNameFromApi) return sensorNameFromApi;
        const bins = siteLayout?.bins ?? sites.find(s => s.siteGuid === selectedSite)?.bins;
        if (!bins?.length) return `Sensor ${sensorId}`;
        const bin = bins.find(b => (b.binId === binId || b.binDataId === binId || b.number === binId));
        const cableList = bin?.cableList ?? bin?.CableList;
        const cable = cableList?.find(c => (c.id === cableId || c.cableId === cableId || c.CableId === cableId));
        const sensorList = cable?.sensorList ?? cable?.SensorList;
        const sensor = sensorList?.find(s => (s.id === sensorId || s.sensorId === sensorId || s.SensorId === sensorId));
        return sensor?.name ?? sensor?.sensorName ?? sensor?.Name ?? `Sensor ${sensorId}`;
    };

    const getSensorLabel = (sg) => getSensorName(sg.binId, sg.cableId, sg.sensorId, sg.sensorName);

    const gradeColor = (grade) => {
        if (grade == null || isNaN(grade)) return getThemeColor('#666666', '#a8abb1');
        const g = Math.max(0, Math.min(100, Number(grade)));
        if (g < 60) return '#e74c3c';
        if (g < 80) return '#f39c12';
        return '#27ae60';
    };

    const changeColor = (delta) => {
        if (delta == null || isNaN(delta)) return getThemeColor('#666666', '#a8abb1');
        if (delta > 0) return '#27ae60';
        if (delta < 0) return '#e74c3c';
        return getThemeColor('#666666', '#a8abb1');
    };

    const buildComparisonTree = () => {
        const dataA = dateDataMap[compareDateA];
        const dataB = dateDataMap[compareDateB];
        const dateItemA = dates.find(d => (d?.dateStr ?? d) === compareDateA);
        const dateItemB = dates.find(d => (d?.dateStr ?? d) === compareDateB);
        const siteGradeA = dateItemA?.grade;
        const siteGradeB = dateItemB?.grade;
        const binsA = dataA?.bins ?? [];
        const binsB = dataB?.bins ?? [];
        const cablesA = dataA?.cables ?? [];
        const cablesB = dataB?.cables ?? [];
        const sensorsA = dataA?.sensors ?? [];
        const sensorsB = dataB?.sensors ?? [];
        const allBinIds = [...new Set([...binsA.map(b => b.binId), ...binsB.map(b => b.binId)])].sort((a, b) => a - b);
        const bins = allBinIds.map(binId => {
            const gA = binsA.find(b => b.binId === binId)?.grade ?? null;
            const gB = binsB.find(b => b.binId === binId)?.grade ?? null;
            const cableKeys = [...new Set([
                ...cablesA.filter(c => c.binId === binId).map(c => c.cableId),
                ...cablesB.filter(c => c.binId === binId).map(c => c.cableId),
            ])].sort((a, b) => a - b);
            const cables = cableKeys.map(cableId => {
                const cgA = cablesA.find(c => c.binId === binId && c.cableId === cableId);
                const cgB = cablesB.find(c => c.binId === binId && c.cableId === cableId);
                const cableName = cgA?.cableName ?? cgB?.cableName;
                const sensorKeys = [...new Set([
                    ...sensorsA.filter(s => s.binId === binId && s.cableId === cableId).map(s => s.sensorId),
                    ...sensorsB.filter(s => s.binId === binId && s.cableId === cableId).map(s => s.sensorId),
                ])].sort((a, b) => a - b);
                const sensors = sensorKeys.map(sensorId => {
                    const sA = sensorsA.find(s => s.binId === binId && s.cableId === cableId && s.sensorId === sensorId);
                    const sB = sensorsB.find(s => s.binId === binId && s.cableId === cableId && s.sensorId === sensorId);
                    const sgA = sA?.grade ?? null;
                    const sgB = sB?.grade ?? null;
                    const sensorName = sA?.sensorName ?? sB?.sensorName;
                    const hasLowA = sgA == null || (typeof sgA === 'number' && !isNaN(sgA) && sgA < 100);
                    const hasLowB = sgB == null || (typeof sgB === 'number' && !isNaN(sgB) && sgB < 100);
                    const label = getSensorName(binId, cableId, sensorId, sensorName);
                    return {
                        sensorId, label, gradeA: sgA, gradeB: sgB, change: sgB != null && sgA != null ? sgB - sgA : null,
                        timeToReadA: sA?.timeToRead ?? sA?.TimeToRead, timeToReadB: sB?.timeToRead ?? sB?.TimeToRead,
                        stabilizedAtReadingA: sA?.stabilizedAtReading ?? sA?.StabilizedAtReading, stabilizedAtReadingB: sB?.stabilizedAtReading ?? sB?.StabilizedAtReading,
                        averageFluctuationA: sA?.averageFluctuation ?? sA?.AverageFluctuation, averageFluctuationB: sB?.averageFluctuation ?? sB?.AverageFluctuation,
                        invalidReadingsCountA: sA?.invalidReadingsCount ?? sA?.InvalidReadingsCount, invalidReadingsCountB: sB?.invalidReadingsCount ?? sB?.InvalidReadingsCount,
                        errorTypeA: sA?.errorType ?? sA?.ErrorType, errorTypeB: sB?.errorType ?? sB?.ErrorType,
                        stabilizationCountA: sA?.stabilizationCount ?? sA?.StabilizationCount, stabilizationCountB: sB?.stabilizationCount ?? sB?.StabilizationCount,
                        finalValueA: sA?.finalValue ?? sA?.FinalValue, finalValueB: sB?.finalValue ?? sB?.FinalValue,
                        resistanceA: sA?.resistance ?? sA?.Resistance, resistanceB: sB?.resistance ?? sB?.Resistance,
                    };
                });
                return { cableId, label: getCableName(binId, cableId, cableName), gradeA: cgA?.grade ?? null, gradeB: cgB?.grade ?? null, change: (cgB?.grade != null && cgA?.grade != null) ? cgB.grade - cgA.grade : null, sensors };
            });
            return { binId, label: getBinName(binId), gradeA: gA, gradeB: gB, change: gB != null && gA != null ? gB - gA : null, cables };
        });
        return { site: { label: 'Site', gradeA: siteGradeA, gradeB: siteGradeB, change: siteGradeB != null && siteGradeA != null ? siteGradeB - siteGradeA : null }, bins };
    };

    const buildTrendsTree = () => {
        const sortedDates = [...dates].sort((a, b) => {
            const dA = new Date((a?.dateStr ?? a) || 0).getTime();
            const dB = new Date((b?.dateStr ?? b) || 0).getTime();
            return dA - dB;
        });
        const dateStrs = sortedDates.map(d => d?.dateStr ?? d);
        const getMetricValue = (item, metric) => {
            if (!item) return null;
            if (metric === 'Grade') return item.grade != null && !isNaN(item.grade) ? Number(item.grade) : null;
            if (metric === 'TimeToRead') return item.timeToRead ?? item.TimeToRead ?? null;
            if (metric === 'Resistance') return item.resistance ?? item.Resistance ?? null;
            if (metric === 'AverageFluctuation') return item.averageFluctuation ?? item.AverageFluctuation ?? null;
            return null;
        };
        const bins = [];
        const allBinIds = new Set();
        dateStrs.forEach(dateStr => {
            const data = dateDataMap[dateStr];
            if (!data?.bins) return;
            (data.bins || []).forEach(b => allBinIds.add(b.binId));
        });
        [...allBinIds].sort((a, b) => a - b).forEach(binId => {
            const cables = [];
            const allCableIds = new Set();
            dateStrs.forEach(dateStr => {
                const data = dateDataMap[dateStr];
                if (!data?.cables) return;
                (data.cables || []).filter(c => c.binId === binId).forEach(c => allCableIds.add(c.cableId));
            });
            [...allCableIds].sort((a, b) => a - b).forEach(cableId => {
                const sensors = [];
                const allSensorIds = new Set();
                dateStrs.forEach(dateStr => {
                    const data = dateDataMap[dateStr];
                    if (!data?.sensors) return;
                    (data.sensors || []).filter(s => s.binId === binId && s.cableId === cableId).forEach(s => allSensorIds.add(s.sensorId));
                });
                [...allSensorIds].sort((a, b) => a - b).forEach(sensorId => {
                    const valuesByDate = dateStrs.map(dateStr => {
                        const data = dateDataMap[dateStr];
                        const sg = (data?.sensors || []).find(s => s.binId === binId && s.cableId === cableId && s.sensorId === sensorId);
                        return getMetricValue(sg, trendsMetric);
                    });
                    const sensor = dateStrs.map(ds => {
                        const data = dateDataMap[ds];
                        return (data?.sensors || []).find(s => s.binId === binId && s.cableId === cableId && s.sensorId === sensorId);
                    }).find(Boolean);
                    const label = sensor ? getSensorLabel(sensor) : getSensorName(binId, cableId, sensorId, null);
                    sensors.push({ sensorId, label, valuesByDate });
                });
                const cable = dateStrs.map(ds => {
                    const data = dateDataMap[ds];
                    return (data?.cables || []).find(c => c.binId === binId && c.cableId === cableId);
                }).find(Boolean);
                const cableName = cable?.cableName ?? cable?.CableName;
                const label = getCableName(binId, cableId, cableName);
                const valuesByDate = dateStrs.map(dateStr => {
                    const data = dateDataMap[dateStr];
                    const cg = (data?.cables || []).find(c => c.binId === binId && c.cableId === cableId);
                    return getMetricValue(cg, trendsMetric);
                });
                cables.push({ cableId, label, valuesByDate, sensors });
            });
            const bin = dateStrs.map(ds => {
                const data = dateDataMap[ds];
                return (data?.bins || []).find(b => b.binId === binId);
            }).find(Boolean);
            const label = getBinName(binId);
            const valuesByDate = dateStrs.map(dateStr => {
                const data = dateDataMap[dateStr];
                const bg = (data?.bins || []).find(b => b.binId === binId);
                return getMetricValue(bg, trendsMetric);
            });
            bins.push({ binId, label, valuesByDate, cables });
        });
        const siteValuesByDate = dateStrs.map(dateStr => {
            const item = dates.find(d => (d?.dateStr ?? d) === dateStr);
            return getMetricValue(item, trendsMetric);
        });
        return { site: { label: 'Site', valuesByDate: siteValuesByDate }, bins, dateStrs };
    };

    const filterTrendsTree = (tree) => {
        const filterBins = (bins) => (bins || []).map(bin => {
            const filteredCables = (bin.cables || []).map(cable => {
                const filteredSensors = (cable.sensors || []).filter(s => labelMatchesFilter(s.label));
                const cableMatch = labelMatchesFilter(cable.label);
                const hasMatchingSensors = filteredSensors.length > 0;
                const show = cableMatch || hasMatchingSensors;
                return show ? { ...cable, sensors: filteredSensors } : null;
            }).filter(Boolean);
            const binMatch = labelMatchesFilter(bin.label);
            const hasMatchingCables = filteredCables.length > 0;
            const show = binMatch || hasMatchingCables;
            return show ? { ...bin, cables: filteredCables } : null;
        }).filter(Boolean);
        return { ...tree, bins: filterBins(tree.bins || []), dateStrs: tree.dateStrs || [] };
    };

    const labelMatchesFilter = (label) => {
        const q = (filterName || '').trim().toLowerCase();
        if (!q) return true;
        return (label || '').toLowerCase().includes(q);
    };

    const itemMatchesFilters = () => true;

    const filterCompareTree = (tree) => {
        const filterBins = (bins) => bins.map(bin => {
            const filteredCables = bin.cables.map(cable => {
                const filteredSensors = cable.sensors.filter(s =>
                    itemMatchesFilters(s.gradeA, s.gradeB, s.change) && labelMatchesFilter(s.label)
                );
                const cableGradeMatch = itemMatchesFilters(cable.gradeA, cable.gradeB, cable.change);
                const cableNameMatch = labelMatchesFilter(cable.label);
                const hasMatchingSensors = filteredSensors.length > 0;
                const show = (cableGradeMatch && cableNameMatch) || hasMatchingSensors;
                return show ? { ...cable, sensors: filteredSensors } : null;
            }).filter(Boolean);
            const binGradeMatch = itemMatchesFilters(bin.gradeA, bin.gradeB, bin.change);
            const binNameMatch = labelMatchesFilter(bin.label);
            const hasMatchingCables = filteredCables.length > 0;
            const show = (binGradeMatch && binNameMatch) || hasMatchingCables;
            return show ? { ...bin, cables: filteredCables } : null;
        }).filter(Boolean);
        return { ...tree, bins: filterBins(tree.bins) };
    };

    const sortCompareTree = (tree) => {
        if (!sortBy) return tree;
        const getVal = (item) => {
            if (sortBy === 'A') return item.gradeA != null && !isNaN(item.gradeA) ? Number(item.gradeA) : -Infinity;
            if (sortBy === 'B') return item.gradeB != null && !isNaN(item.gradeB) ? Number(item.gradeB) : -Infinity;
            return item.change != null && !isNaN(item.change) ? Number(item.change) : -Infinity;
        };
        const cmp = (a, b) => {
            const va = getVal(a);
            const vb = getVal(b);
            if (va !== vb) return sortDir === 'asc' ? (va - vb) : (vb - va);
            return 0;
        };
        const sortedBins = [...tree.bins].sort((a, b) => cmp(a, b));
        const bins = sortedBins.map(bin => ({
            ...bin,
            cables: [...bin.cables].sort((a, b) => cmp(a, b)).map(cable => ({
                ...cable,
                sensors: [...cable.sensors].sort((a, b) => cmp(a, b)),
            })),
        }));
        return { ...tree, bins };
    };

    const handleSort = (col) => {
        setSortBy(col);
        setSortDir(prev => (sortBy === col ? (prev === 'asc' ? 'desc' : 'asc') : 'asc'));
    };

    const CompareRowLabel = ({ label, gradeA, gradeB, change }) => (
        <MuiMaterials.Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 0.25, width: '100%', minWidth: 0, fontSize: '0.75rem' }}>
            <span style={{ flex: '1 1 0', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>{label}</span>
            <span style={{ flex: '0 0 auto', color: gradeColor(gradeA), fontWeight: 600, minWidth: 24, textAlign: 'right' }}>{gradeA != null && !isNaN(gradeA) ? Math.round(gradeA) : '-'}</span>
            <span style={{ flex: '0 0 auto', color: gradeColor(gradeB), fontWeight: 600, minWidth: 24, textAlign: 'right' }}>{gradeB != null && !isNaN(gradeB) ? Math.round(gradeB) : '-'}</span>
            <span style={{ flex: '0 0 auto', color: changeColor(change), fontWeight: 600, minWidth: 28, textAlign: 'right' }}>{change != null && !isNaN(change) ? (change > 0 ? '+' : '') + Math.round(change) : '-'}</span>
        </MuiMaterials.Box>
    );

    const SensorCompareRowLabel = ({ sensor }) => (
        <MuiMaterials.Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 0.25, width: '100%', minWidth: 0, fontSize: '0.7rem', flexWrap: 'wrap' }}>
            <span style={{ flex: '1 1 0', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>{sensor.label}</span>
            <DataCell value={sensor.timeToReadB} title="Time to Read" style={{ flex: '0 0 auto', minWidth: 20, textAlign: 'right' }} />
            <DataCell value={sensor.stabilizedAtReadingB} title="Stabilized at Reading" style={{ flex: '0 0 auto', minWidth: 20, textAlign: 'right' }} />
            <DataCell value={sensor.averageFluctuationB} title="Average Fluctuation" style={{ flex: '0 0 auto', minWidth: 20, textAlign: 'right' }} roundTo={1} />
            <DataCell value={sensor.invalidReadingsCountB} title="Invalid Readings Count" style={{ flex: '0 0 auto', minWidth: 18, textAlign: 'right' }} />
            <DataCell value={sensor.errorTypeB} title="Error Type" style={{ flex: '0 0 auto', minWidth: 18, textAlign: 'right' }} />
            <DataCell value={sensor.stabilizationCountB} title="Stabilization Count" style={{ flex: '0 0 auto', minWidth: 20, textAlign: 'right' }} />
            <DataCell value={sensor.finalValueB} title="Final Value" style={{ flex: '0 0 auto', minWidth: 24, textAlign: 'right' }} roundTo={1} />
            <DataCell value={sensor.resistanceB} title="Resistance (B)" style={{ flex: '0 0 auto', minWidth: 24, textAlign: 'right' }} />
            <span style={{ flex: '0 0 auto', color: gradeColor(sensor.gradeA), fontWeight: 600, minWidth: 24, textAlign: 'right' }}>{sensor.gradeA != null && !isNaN(sensor.gradeA) ? Math.round(sensor.gradeA) : '-'}</span>
            <span style={{ flex: '0 0 auto', color: gradeColor(sensor.gradeB), fontWeight: 600, minWidth: 24, textAlign: 'right' }}>{sensor.gradeB != null && !isNaN(sensor.gradeB) ? Math.round(sensor.gradeB) : '-'}</span>
            <span style={{ flex: '0 0 auto', color: changeColor(sensor.change), fontWeight: 600, minWidth: 28, textAlign: 'right' }}>{sensor.change != null && !isNaN(sensor.change) ? (sensor.change > 0 ? '+' : '') + Math.round(sensor.change) : '-'}</span>
        </MuiMaterials.Box>
    );

    const DataCell = ({ value, title, style = {}, roundTo }) => {
        let display = value != null && value !== '' ? value : '-';
        if (roundTo != null && typeof value === 'number' && !isNaN(value)) display = Number(value).toFixed(roundTo);
        return (
            <MuiMaterials.Tooltip title={title} placement="top" arrow>
                <span style={style}>{display}</span>
            </MuiMaterials.Tooltip>
        );
    };

    const formatTrendValue = (val, metric) => {
        if (val == null || val === '') return '-';
        if (typeof val === 'number' && !isNaN(val)) {
            if (metric === 'Grade') return Math.round(val);
            if (metric === 'AverageFluctuation') return Number(val).toFixed(1);
            return val;
        }
        return String(val);
    };

    const TrendsRowLabel = ({ label, valuesByDate, metric, dateStrs }) => (
        <MuiMaterials.Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, width: '100%', minWidth: 0, fontSize: '0.75rem', flexWrap: 'nowrap', overflowX: 'auto' }}>
            <span style={{ flex: '0 0 auto', minWidth: 80, maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis' }}>{label}</span>
            {(valuesByDate || []).map((v, i) => (
                <MuiMaterials.Tooltip key={i} title={dateStrs?.[i] ? formatDate(dateStrs[i]) : ''} placement="top" arrow>
                    <span
                        style={{
                            flex: '0 0 auto',
                            minWidth: 28,
                            textAlign: 'right',
                            color: metric === 'Grade' ? gradeColor(v) : getThemeColor('#333333', '#f4cc8e'),
                            fontWeight: metric === 'Grade' ? 600 : 400,
                        }}
                    >
                        {formatTrendValue(v, metric)}
                    </span>
                </MuiMaterials.Tooltip>
            ))}
        </MuiMaterials.Box>
    );

    const SensorTreeRowLabel = ({ sg }) => (
        <MuiMaterials.Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 0.25, width: '100%', minWidth: 0, fontSize: '0.7rem', flexWrap: 'wrap' }}>
            <span style={{ flex: '1 1 0', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>{getSensorLabel(sg)}</span>
            <DataCell value={sg.timeToRead} title="Time to Read" style={{ flex: '0 0 auto', minWidth: 24, textAlign: 'right' }} />
            <DataCell value={sg.stabilizedAtReading} title="Stabilized at Reading" style={{ flex: '0 0 auto', minWidth: 20, textAlign: 'right' }} />
            <DataCell value={sg.averageFluctuation} title="Average Fluctuation" style={{ flex: '0 0 auto', minWidth: 20, textAlign: 'right' }} />
            <DataCell value={sg.invalidReadingsCount} title="Invalid Readings Count" style={{ flex: '0 0 auto', minWidth: 18, textAlign: 'right' }} />
            <DataCell value={sg.errorType} title="Error Type" style={{ flex: '0 0 auto', minWidth: 18, textAlign: 'right' }} />
            <DataCell value={sg.stabilizationCount} title="Stabilization Count" style={{ flex: '0 0 auto', minWidth: 20, textAlign: 'right' }} />
            <DataCell value={sg.finalValue} title="Final Value" style={{ flex: '0 0 auto', minWidth: 24, textAlign: 'right' }} roundTo={1} />
            <DataCell value={sg.resistance} title="Resistance" style={{ flex: '0 0 auto', minWidth: 24, textAlign: 'right' }} />
            <span style={{ flex: '0 0 auto', color: gradeColor(sg.grade), fontWeight: 600, minWidth: 32, textAlign: 'right' }}>{sg.grade != null && !isNaN(sg.grade) ? Math.round(sg.grade) : '-'}</span>
        </MuiMaterials.Box>
    );

    const renderModal = () => {
        const activeSiteGuid =
            selectedSite || (isModalOpen[0] ? initialSiteGuid : '') || '';
        const phase = activeSiteGuid ? 'edit' : 'select';
        const handleClose = () => setIsModalOpen([false]);

        return (
        <ModalChrome
            open={isModalOpen[0]}
            onClose={handleClose}
            title="Site Grades"
            toolTitle="Grades"
            siteWindowId={SITE_WINDOW_IDS.grades}
            siteGuid={activeSiteGuid || null}
            showHeaderSiteSelect={phase === 'edit'}
            onHeaderSiteChange={(guid) => {
                setPickerSiteGuid(guid);
                setSelectedSite(guid);
            }}
            headerSites={sites}
            className="siteFilterModal hide-scrollbar"
            loadingOverlay={phase === 'edit' && loading ? <CenteredLoadingOverlay /> : null}
        >
            {phase === 'select' ? (
                <SiteSelectPhase
                    value={pickerSiteGuid}
                    onChange={setPickerSiteGuid}
                    onContinue={(guid) => {
                        setPickerSiteGuid(guid);
                        setSelectedSite(guid);
                    }}
                    onCancel={handleClose}
                    loading={sitesLoading}
                    filterSite={(s) =>
                        !s.isHms && sites.some((x) => x.siteGuid === s.siteGuid)
                    }
                />
            ) : (
            <ModalBody className="siteFilterModalContent hide-scrollbar" sx={{ overflowY: "auto" }}>
                <MuiMaterials.Box sx={{ display: 'flex', gap: 0, marginBottom: 1, borderBottom: `1px solid ${getThemeColor('#e0e0e0', '#3d3d3d')}`, flexShrink: 0 }}>
                    {['Grade History', 'Compare dates', 'Trends'].map((label, i) => (
                        <MuiMaterials.Button
                            key={label}
                            onClick={() => setViewTab(i)}
                            sx={{
                                color: viewTab === i ? getThemeColor('#333333', '#f4cc8e') : getThemeColor('#666666', '#a8abb1'),
                                borderBottom: viewTab === i ? `2px solid ${getThemeColor('#333333', '#f4cc8e')}` : '2px solid transparent',
                                borderRadius: 0,
                                minWidth: 'auto',
                                px: 2,
                                py: 1,
                                textTransform: 'none',
                            }}
                        >
                            {label}
                        </MuiMaterials.Button>
                    ))}
                </MuiMaterials.Box>
                {viewTab === 1 && (
                    <MuiMaterials.Box
                        sx={{
                            width: "100%",
                            marginBottom: "16px",
                            px: 2,
                            py: 1.5,
                            background: getThemeColor('#f8f8f8', '#252525'),
                            borderRadius: "8px",
                            border: `1px solid ${getThemeColor('#e0e0e0', '#3d3d3d')}`,
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: 2,
                            alignItems: 'center',
                            justifyContent: 'flex-start',
                            flexShrink: 0,
                            boxSizing: 'border-box',
                        }}
                    >
                        <MuiMaterials.FormControl size="small" variant="outlined" sx={{
                            minWidth: 140,
                            '& .MuiInputLabel-root': { color: getThemeColor('#666666', '#a8abb1') },
                            '& .MuiInputLabel-root.Mui-focused': { color: getThemeColor('#333333', '#f4cc8e') },
                            '& .MuiInputLabel-outlined:not(.MuiInputLabel-shrink)': {
                                transform: 'translate(14px, 9px) scale(1)',
                            },
                            '& .MuiOutlinedInput-root': {
                                height: 40,
                                color: getThemeColor('#333333', '#f4cc8e'),
                                backgroundColor: getThemeColor('#ffffff', '#2d2d2d'),
                                alignItems: 'center',
                                '& fieldset': { borderColor: getThemeColor('#cccccc', 'rgba(244,204,142,0.5)') },
                                '&:hover fieldset': { borderColor: getThemeColor('#666666', '#f4cc8e') },
                                '&.Mui-focused fieldset': { borderColor: getThemeColor('#666666', '#f4cc8e') },
                                '& .MuiSvgIcon-root': { color: getThemeColor('#666666', '#a8abb1') },
                            },
                            '& .MuiSelect-select': {
                                display: 'flex',
                                alignItems: 'center',
                                py: 0,
                                height: '100%',
                                boxSizing: 'border-box',
                            },
                        }}>
                            <MuiMaterials.InputLabel>Date A</MuiMaterials.InputLabel>
                            <MuiMaterials.Select
                                value={compareDateA}
                                onChange={(e) => setCompareDateA(e.target.value)}
                                label="Date A"
                                MenuProps={withScrollHintMenuProps({
                                    PaperProps: {
                                        sx: {
                                            backgroundColor: getThemeColor('#ffffff', '#2d2d2d'),
                                            '& .MuiMenuItem-root': { color: getThemeColor('#333333', '#f4cc8e') },
                                        },
                                    },
                                })}
                            >
                                {dates.map((item) => {
                                    const ds = item?.dateStr ?? item;
                                    return <MuiMaterials.MenuItem key={ds} value={ds}>{formatDate(ds)}</MuiMaterials.MenuItem>;
                                })}
                            </MuiMaterials.Select>
                        </MuiMaterials.FormControl>
                        <MuiMaterials.IconButton
                            onClick={() => { setCompareDateA(compareDateB); setCompareDateB(compareDateA); }}
                            sx={{
                                color: getThemeColor('#666666', '#a8abb1'),
                                alignSelf: 'center',
                            }}
                            title="Swap dates"
                        >
                            <MuiMaterials.SwapHorizIcon />
                        </MuiMaterials.IconButton>
                        <MuiMaterials.FormControl size="small" variant="outlined" sx={{
                            minWidth: 140,
                            '& .MuiInputLabel-root': { color: getThemeColor('#666666', '#a8abb1') },
                            '& .MuiInputLabel-root.Mui-focused': { color: getThemeColor('#333333', '#f4cc8e') },
                            '& .MuiInputLabel-outlined:not(.MuiInputLabel-shrink)': {
                                transform: 'translate(14px, 9px) scale(1)',
                            },
                            '& .MuiOutlinedInput-root': {
                                height: 40,
                                color: getThemeColor('#333333', '#f4cc8e'),
                                backgroundColor: getThemeColor('#ffffff', '#2d2d2d'),
                                alignItems: 'center',
                                '& fieldset': { borderColor: getThemeColor('#cccccc', 'rgba(244,204,142,0.5)') },
                                '&:hover fieldset': { borderColor: getThemeColor('#666666', '#f4cc8e') },
                                '&.Mui-focused fieldset': { borderColor: getThemeColor('#666666', '#f4cc8e') },
                                '& .MuiSvgIcon-root': { color: getThemeColor('#666666', '#a8abb1') },
                            },
                            '& .MuiSelect-select': {
                                display: 'flex',
                                alignItems: 'center',
                                py: 0,
                                height: '100%',
                                boxSizing: 'border-box',
                            },
                        }}>
                            <MuiMaterials.InputLabel>Date B</MuiMaterials.InputLabel>
                            <MuiMaterials.Select
                                value={compareDateB}
                                onChange={(e) => setCompareDateB(e.target.value)}
                                label="Date B"
                                MenuProps={withScrollHintMenuProps({
                                    PaperProps: {
                                        sx: {
                                            backgroundColor: getThemeColor('#ffffff', '#2d2d2d'),
                                            '& .MuiMenuItem-root': { color: getThemeColor('#333333', '#f4cc8e') },
                                        },
                                    },
                                })}
                            >
                                {dates.map((item) => {
                                    const ds = item?.dateStr ?? item;
                                    return <MuiMaterials.MenuItem key={ds} value={ds}>{formatDate(ds)}</MuiMaterials.MenuItem>;
                                })}
                            </MuiMaterials.Select>
                        </MuiMaterials.FormControl>
                    </MuiMaterials.Box>
                )}
                {viewTab === 2 && (
                    <MuiMaterials.Box
                        sx={{
                            width: "100%",
                            marginBottom: "16px",
                            padding: "16px",
                            background: getThemeColor('#f8f8f8', '#252525'),
                            borderRadius: "8px",
                            border: `1px solid ${getThemeColor('#e0e0e0', '#3d3d3d')}`,
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: 2,
                            alignItems: 'center',
                        }}
                    >
                        <MuiMaterials.TextField
                            size="small"
                            placeholder="Filter by name..."
                            value={filterName}
                            onChange={(e) => setFilterName(e.target.value)}
                            sx={{
                                minWidth: 140,
                                '& .MuiInputLabel-root': { color: getThemeColor('#666666', '#a8abb1') },
                                '& .MuiOutlinedInput-root': {
                                    color: getThemeColor('#333333', '#f4cc8e'),
                                    backgroundColor: getThemeColor('#ffffff', '#2d2d2d'),
                                    '& fieldset': { borderColor: getThemeColor('#cccccc', 'rgba(244,204,142,0.5)') },
                                    '&:hover fieldset': { borderColor: getThemeColor('#666666', '#f4cc8e') },
                                    '&.Mui-focused fieldset': { borderColor: getThemeColor('#666666', '#f4cc8e') },
                                },
                            }}
                        />
                        <MuiMaterials.FormControl size="small" variant="outlined" sx={{
                            minWidth: 140,
                            '& .MuiInputLabel-root': { color: getThemeColor('#666666', '#a8abb1') },
                            '& .MuiOutlinedInput-root': {
                                color: getThemeColor('#333333', '#f4cc8e'),
                                backgroundColor: getThemeColor('#ffffff', '#2d2d2d'),
                                '& fieldset': { borderColor: getThemeColor('#cccccc', 'rgba(244,204,142,0.5)') },
                            },
                        }}>
                            <MuiMaterials.InputLabel>Metric</MuiMaterials.InputLabel>
                            <MuiMaterials.Select
                                value={trendsMetric}
                                onChange={(e) => setTrendsMetric(e.target.value)}
                                label="Metric"
                                MenuProps={withScrollHintMenuProps({
                                    PaperProps: {
                                        sx: {
                                            backgroundColor: getThemeColor('#ffffff', '#2d2d2d'),
                                            '& .MuiMenuItem-root': { color: getThemeColor('#333333', '#f4cc8e') },
                                        },
                                    },
                                })}
                            >
                                <MuiMaterials.MenuItem value="Grade">Grade</MuiMaterials.MenuItem>
                                <MuiMaterials.MenuItem value="TimeToRead">TimeToRead</MuiMaterials.MenuItem>
                                <MuiMaterials.MenuItem value="Resistance">Resistance</MuiMaterials.MenuItem>
                                <MuiMaterials.MenuItem value="AverageFluctuation">AverageFluctuation</MuiMaterials.MenuItem>
                            </MuiMaterials.Select>
                        </MuiMaterials.FormControl>
                    </MuiMaterials.Box>
                )}
                <ModalContentTile
                    sx={{
                        maxHeight: "calc(100vh - 280px)",
                        minHeight: "200px",
                    }}
                >
                    {viewTab === 1 && compareDateA && compareDateB ? (
                        (() => {
                            const dataA = dateDataMap[compareDateA];
                            const dataB = dateDataMap[compareDateB];
                            const loadingA = dataA?.loading;
                            const loadingB = dataB?.loading;
                            if (loadingA || loadingB) {
                                return (
                                    <MuiMaterials.Box
                                        sx={{
                                            flex: 1,
                                            minHeight: 200,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                        }}
                                    >
                                        <Spinner size="modal" />
                                    </MuiMaterials.Box>
                                );
                            }
                            const tree = sortCompareTree(filterCompareTree(buildComparisonTree()));
                            const headerBg = getThemeColor('#f5f5f5', '#3d3d3d');
                            const headerColor = getThemeColor('#333333', '#f4cc8e');
                            const inputSx = {
                                width: 56,
                                minWidth: 48,
                                fontSize: '0.75rem',
                                '& .MuiOutlinedInput-root': {
                                    color: getThemeColor('#333333', '#f4cc8e'),
                                    fontSize: '0.75rem',
                                    '& fieldset': { borderColor: getThemeColor('#ccc', 'rgba(244,204,142,0.4)') },
                                },
                            };
                            return (
                                <MuiMaterials.Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
                                    <MuiMaterials.Box
                                        sx={{
                                            flexShrink: 0,
                                            background: getThemeColor('#ffffff', '#2d2d2d'),
                                            borderBottom: `1px solid ${getThemeColor('#e0e0e0', '#3d3d3d')}`,
                                            boxShadow: getThemeColor('0 4px 6px -1px rgba(0,0,0,0.1), 0 2px 4px -2px rgba(0,0,0,0.1)', '0 4px 6px -1px rgba(0,0,0,0.3), 0 2px 4px -2px rgba(0,0,0,0.2)'),
                                        }}
                                    >
                                        <MuiMaterials.Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, px: 1, py: 0.5, paddingRight: '0px', paddingLeft: '0px', background: getThemeColor('#fafafa', '#2a2a2a'), color: getThemeColor('#666', '#a8abb1'), fontSize: '0.75rem', flexShrink: 0, flexWrap: 'wrap', minWidth: 0 }}>
                                            
                                            <MuiMaterials.TextField size="small" placeholder="Name" value={filterName} onChange={(e) => setFilterName(e.target.value)} sx={{ ...inputSx, flex: '1 1 80px', minWidth: 60 }} />
                                        </MuiMaterials.Box>
                                        <MuiMaterials.Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, px: 1, py: 0.5, fontWeight: 600, background: headerBg, color: headerColor, fontSize: '0.7rem', flexShrink: 0, minWidth: 0 }}>
                                            <span style={{ flex: '1 1 0', minWidth: 0 }}>Item</span>
                                            <MuiMaterials.Box component="span" onClick={() => handleSort('A')} sx={{ flex: '0 0 auto', minWidth: 24, textAlign: 'right', cursor: 'pointer', userSelect: 'none' }}>A{sortBy === 'A' ? (sortDir === 'asc' ? ' ↑' : ' ↓') : ''}</MuiMaterials.Box>
                                            <MuiMaterials.Box component="span" onClick={() => handleSort('B')} sx={{ flex: '0 0 auto', minWidth: 24, textAlign: 'right', cursor: 'pointer', userSelect: 'none' }}>B{sortBy === 'B' ? (sortDir === 'asc' ? ' ↑' : ' ↓') : ''}</MuiMaterials.Box>
                                            <MuiMaterials.Box component="span" onClick={() => handleSort('delta')} sx={{ flex: '0 0 auto', minWidth: 28, textAlign: 'right', cursor: 'pointer', userSelect: 'none' }}>Δ{sortBy === 'delta' ? (sortDir === 'asc' ? ' ↑' : ' ↓') : ''}</MuiMaterials.Box>
                                        </MuiMaterials.Box>
                                    </MuiMaterials.Box>
                                    <MuiMaterials.Box className="hide-scrollbar" sx={{ flex: 1, minHeight: 0, minWidth: 0, overflowY: 'auto', overflowX: 'hidden' }}>
                                        <MuiMaterials.TreeView
                                defaultCollapseIcon={<MuiMaterials.ExpandMoreIcon />}
                                defaultExpandIcon={<MuiMaterials.ChevronRightIcon />}
                                sx={{
                                    color: getThemeColor('#333333', '#f4cc8e'),
                                    overflow: 'visible',
                                    minWidth: 0,
                                    '& .MuiTreeItem-content': { color: getThemeColor('#333333', '#f4cc8e'), minWidth: 0 },
                                }}
                                    >
                                            <MuiMaterials.TreeItem
                                                itemId="compare-site"
                                                label={<CompareRowLabel label={tree.site.label} gradeA={tree.site.gradeA} gradeB={tree.site.gradeB} change={tree.site.change} />}
                                            >
                                                {tree.bins.map((bin) => (
                                                    <MuiMaterials.TreeItem
                                                        key={`compare-bin-${bin.binId}`}
                                                        itemId={`compare-bin-${bin.binId}`}
                                                        label={<CompareRowLabel label={bin.label} gradeA={bin.gradeA} gradeB={bin.gradeB} change={bin.change} />}
                                                    >
                                                        {bin.cables.map((cable) => (
                                                            <MuiMaterials.TreeItem
                                                                key={`compare-cable-${bin.binId}-${cable.cableId}`}
                                                                itemId={`compare-cable-${bin.binId}-${cable.cableId}`}
                                                                label={<CompareRowLabel label={cable.label} gradeA={cable.gradeA} gradeB={cable.gradeB} change={cable.change} />}
                                                            >
                                                                {cable.sensors.map((sensor) => (
                                                                    <MuiMaterials.TreeItem
                                                                        key={`compare-sensor-${bin.binId}-${cable.cableId}-${sensor.sensorId}`}
                                                                        itemId={`compare-sensor-${bin.binId}-${cable.cableId}-${sensor.sensorId}`}
                                                                        label={<SensorCompareRowLabel sensor={sensor} />}
                                                                    />
                                                                ))}
                                                            </MuiMaterials.TreeItem>
                                                                ))}
                                                            </MuiMaterials.TreeItem>
                                                        ))}
                                                    </MuiMaterials.TreeItem>
                                                </MuiMaterials.TreeView>
                                    </MuiMaterials.Box>
                                </MuiMaterials.Box>
                                    );
                        })()
                    ) : viewTab === 1 ? (
                        <MuiMaterials.Typography sx={{ color: getThemeColor('#666666', '#a8abb1') }}>
                            Select Date A and Date B to compare.
                        </MuiMaterials.Typography>
                    ) : viewTab === 2 ? (
                        (() => {
                            if (loading || dates.length === 0) {
                                return loading ? (
                                    <MuiMaterials.Box
                                        sx={{
                                            flex: 1,
                                            minHeight: 200,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                        }}
                                    >
                                        <Spinner size="modal" />
                                    </MuiMaterials.Box>
                                ) : (
                                    <MuiMaterials.Typography sx={{ color: getThemeColor('#666666', '#a8abb1') }}>
                                        No grade dates found for this site.
                                    </MuiMaterials.Typography>
                                );
                            }
                            const tree = filterTrendsTree(buildTrendsTree());
                            if (!tree.bins || tree.bins.length === 0) {
                                return (
                                    <MuiMaterials.Typography sx={{ color: getThemeColor('#666666', '#a8abb1') }}>
                                        Loading grade data...
                                    </MuiMaterials.Typography>
                                );
                            }
                            return (
                                <MuiMaterials.Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
                                    <MuiMaterials.Box className="hide-scrollbar" sx={{ flex: 1, minHeight: 0, minWidth: 0, overflowY: 'auto', overflowX: 'auto' }}>
                                        <MuiMaterials.TreeView
                                            defaultCollapseIcon={<MuiMaterials.ExpandMoreIcon />}
                                            defaultExpandIcon={<MuiMaterials.ChevronRightIcon />}
                                            sx={{
                                                color: getThemeColor('#333333', '#f4cc8e'),
                                                overflow: 'visible',
                                                minWidth: 0,
                                                '& .MuiTreeItem-content': { color: getThemeColor('#333333', '#f4cc8e'), minWidth: 0 },
                                            }}
                                        >
                                            <MuiMaterials.TreeItem
                                                itemId="trends-site"
                                                label={<TrendsRowLabel label={tree.site.label} valuesByDate={tree.site.valuesByDate} metric={trendsMetric} dateStrs={tree.dateStrs} />}
                                            >
                                                {(tree.bins || []).map((bin) => (
                                                    <MuiMaterials.TreeItem
                                                        key={`trends-bin-${bin.binId}`}
                                                        itemId={`trends-bin-${bin.binId}`}
                                                        label={<TrendsRowLabel label={bin.label} valuesByDate={bin.valuesByDate} metric={trendsMetric} dateStrs={tree.dateStrs} />}
                                                    >
                                                        {(bin.cables || []).map((cable) => (
                                                            <MuiMaterials.TreeItem
                                                                key={`trends-cable-${bin.binId}-${cable.cableId}`}
                                                                itemId={`trends-cable-${bin.binId}-${cable.cableId}`}
                                                                label={<TrendsRowLabel label={cable.label} valuesByDate={cable.valuesByDate} metric={trendsMetric} dateStrs={tree.dateStrs} />}
                                                            >
                                                                {(cable.sensors || []).map((sensor) => (
                                                                    <MuiMaterials.TreeItem
                                                                        key={`trends-sensor-${bin.binId}-${cable.cableId}-${sensor.sensorId}`}
                                                                        itemId={`trends-sensor-${bin.binId}-${cable.cableId}-${sensor.sensorId}`}
                                                                        label={<TrendsRowLabel label={sensor.label} valuesByDate={sensor.valuesByDate} metric={trendsMetric} dateStrs={tree.dateStrs} />}
                                                                    />
                                                                ))}
                                                            </MuiMaterials.TreeItem>
                                                        ))}
                                                    </MuiMaterials.TreeItem>
                                                ))}
                                            </MuiMaterials.TreeItem>
                                        </MuiMaterials.TreeView>
                                    </MuiMaterials.Box>
                                </MuiMaterials.Box>
                            );
                        })()
                    ) : loading ? null : dates.length === 0 ? (
                        <MuiMaterials.Typography sx={{ color: getThemeColor('#666666', '#a8abb1') }}>
                            No grade dates found for this site.
                        </MuiMaterials.Typography>
                    ) : (
                        <MuiMaterials.Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
                            <MuiMaterials.Box
                                sx={{
                                    flexShrink: 0,
                                    background: getThemeColor('#ffffff', '#2d2d2d'),
                                    borderBottom: `1px solid ${getThemeColor('#e0e0e0', '#3d3d3d')}`,
                                    boxShadow: getThemeColor('0 4px 6px -1px rgba(0,0,0,0.1), 0 2px 4px -2px rgba(0,0,0,0.1)', '0 4px 6px -1px rgba(0,0,0,0.3), 0 2px 4px -2px rgba(0,0,0,0.2)'),
                                }}
                            >
                                <MuiMaterials.Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, px: 1, py: 0.5, minWidth: 0, paddingRight: '0px', paddingLeft: '0px' }}>
                                    <MuiMaterials.TextField
                                        size="small"
                                        placeholder="Filter by name..."
                                        value={filterName}
                                        onChange={(e) => setFilterName(e.target.value)}
                                        sx={{
                                            flex: '1 1 0',
                                            minWidth: 0,
                                            '& .MuiOutlinedInput-root': {
                                                color: getThemeColor('#333333', '#f4cc8e'),
                                                fontSize: '0.8rem',
                                                '& fieldset': { borderColor: getThemeColor('#ccc', 'rgba(244,204,142,0.4)') },
                                            },
                                        }}
                                    />
                                </MuiMaterials.Box>
                                <MuiMaterials.Box
                                sx={{
                                    display: 'flex', alignItems: 'center', gap: 0.25, px: 1, py: 0.5, fontWeight: 600,
                                    background: getThemeColor('#f5f5f5', '#3d3d3d'),
                                    color: getThemeColor('#333333', '#f4cc8e'),
                                    fontSize: '0.75rem',
                                    userSelect: 'none',
                                    minWidth: 0,
                                }}
                                >
                                <MuiMaterials.Box component="span" onClick={() => { setTreeSortInnerBy('name'); setTreeSortNameDir(d => d === 'asc' ? 'desc' : 'asc'); }} sx={{ flex: '1 1 0', minWidth: 0, cursor: 'pointer' }}>Item{treeSortInnerBy === 'name' ? (treeSortNameDir === 'asc' ? ' ↑' : ' ↓') : ''}</MuiMaterials.Box>
                                <MuiMaterials.Box component="span" onClick={() => setTreeSortDateDir(d => d === 'asc' ? 'desc' : 'asc')} sx={{ flex: '0 0 auto', minWidth: 28, textAlign: 'right', cursor: 'pointer' }}>Date{treeSortDateDir === 'asc' ? ' ↑' : ' ↓'}</MuiMaterials.Box>
                                <MuiMaterials.Box component="span" onClick={() => { setTreeSortInnerBy('grade'); setTreeSortGradeDir(d => d === 'asc' ? 'desc' : 'asc'); }} sx={{ flex: '0 0 auto', minWidth: 32, textAlign: 'right', cursor: 'pointer' }}>Grade{treeSortInnerBy === 'grade' ? (treeSortGradeDir === 'asc' ? ' ↑' : ' ↓') : ''}</MuiMaterials.Box>
                                </MuiMaterials.Box>
                            </MuiMaterials.Box>
                            <MuiMaterials.Box className="hide-scrollbar" sx={{ flex: 1, minHeight: 0, minWidth: 0, overflowY: 'auto', overflowX: 'hidden' }}>
                                <MuiMaterials.TreeView
                                defaultCollapseIcon={<MuiMaterials.ExpandMoreIcon />}
                                defaultExpandIcon={<MuiMaterials.ChevronRightIcon />}
                                expandedItems={expanded}
                                onExpandedItemsChange={handleExpandedChange}
                                sx={{
                                    color: getThemeColor('#333333', '#f4cc8e'),
                                    overflow: 'visible',
                                    minWidth: 0,
                                    '& .MuiTreeItem-content': {
                                        color: getThemeColor('#333333', '#f4cc8e'),
                                        minWidth: 0,
                                    },
                                }}
                            >
                            {[...dates]
                                .sort((a, b) => {
                                    const dA = new Date((a?.dateStr ?? a) || 0).getTime();
                                    const dB = new Date((b?.dateStr ?? b) || 0).getTime();
                                    const dateCmp = treeSortDateDir === 'asc' ? dA - dB : dB - dA;
                                    if (dateCmp !== 0) return dateCmp;
                                    const gA = a?.grade != null && !isNaN(a.grade) ? Number(a.grade) : -1;
                                    const gB = b?.grade != null && !isNaN(b.grade) ? Number(b.grade) : -1;
                                    return treeSortDateDir === 'asc' ? gA - gB : gB - gA;
                                })
                                .filter((item) => {
                                    const dateStr = item?.dateStr ?? item;
                                    const data = dateDataMap[dateStr];
                                    const bins = data?.bins ?? [];
                                    if (bins.length === 0) return labelMatchesFilter(formatDate(dateStr));
                                    const hasMatchingBin = bins.some(bg => {
                                        const binName = getBinName(bg.binId);
                                        const cablesForBin = (data?.cables ?? []).filter(c => c.binId === bg.binId);
                                        const hasMatchingCable = cablesForBin.some(cg => {
                                            const cableLabel = getCableName(cg.binId, cg.cableId, cg.cableName);
                                            const cableApiName = (cg.cableName ?? cg.CableName ?? '').toString();
                                            const sensorsForCable = (data?.sensors ?? []).filter(s => s.binId === cg.binId && s.cableId === cg.cableId);
                                            const hasMatchingSensor = sensorsForCable.some(s => {
                                                const sensorLabel = getSensorLabel(s);
                                                const sensorApiName = (s.sensorName ?? s.SensorName ?? '').toString();
                                                return labelMatchesFilter(sensorLabel) || labelMatchesFilter(sensorApiName) || labelMatchesFilter(String(s.sensorId));
                                            });
                                            return labelMatchesFilter(cableLabel) || labelMatchesFilter(cableApiName) || labelMatchesFilter(String(cg.cableId)) || hasMatchingSensor;
                                        });
                                        return labelMatchesFilter(binName) || labelMatchesFilter(String(bg.binId)) || hasMatchingCable;
                                    });
                                    return labelMatchesFilter(formatDate(dateStr)) || hasMatchingBin;
                                })
                                .map((item, di) => {
                                const dateStr = item?.dateStr ?? item;
                                const grade = item?.grade;
                                const dateKey = `date-${dateStr}`;
                                const data = dateDataMap[dateStr];
                                const bins = data?.bins ?? [];
                                const isLoading = data?.loading;
                                const dateLabel = grade != null && !isNaN(grade) ? (
                                    <MuiMaterials.Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 0.25, width: '100%', minWidth: 0, fontSize: '0.75rem' }}>
                                        <span style={{ flex: '1 1 0', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>{formatDate(dateStr)}</span>
                                        <span style={{ flex: '0 0 auto', color: gradeColor(grade), fontWeight: 600, minWidth: 32, textAlign: 'right' }}>{Math.round(grade)}</span>
                                    </MuiMaterials.Box>
                                ) : (
                                    <MuiMaterials.Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 0.25, width: '100%', minWidth: 0, fontSize: '0.75rem' }}>
                                        <span style={{ flex: '1 1 0', minWidth: 0 }}>{formatDate(dateStr)}</span>
                                        <span style={{ flex: '0 0 auto', minWidth: 32, textAlign: 'right' }}>-</span>
                                    </MuiMaterials.Box>
                                );
                                return (
                                    <MuiMaterials.TreeItem
                                        key={dateKey}
                                        itemId={dateKey}
                                        label={dateLabel}
                                    >
                                        {isLoading ? (
                                            <MuiMaterials.TreeItem itemId={`${dateKey}-loading`} label="Loading..." />
                                        ) : bins.length === 0 && !isLoading ? (
                                            <MuiMaterials.TreeItem itemId={`${dateKey}-empty`} label="No grades" />
                                        ) : (
                                            (() => {
                                                const binsSorted = [...(data?.bins ?? [])].sort((a, b) => {
                                                    if (treeSortInnerBy === 'name') {
                                                        const nA = (getBinName(a?.binId) ?? '').toLowerCase();
                                                        const nB = (getBinName(b?.binId) ?? '').toLowerCase();
                                                        const cmp = nA.localeCompare(nB, undefined, { sensitivity: 'base' });
                                                        return treeSortNameDir === 'asc' ? cmp : -cmp;
                                                    }
                                                    const gA = a?.grade != null && !isNaN(a.grade) ? Number(a.grade) : -1;
                                                    const gB = b?.grade != null && !isNaN(b.grade) ? Number(b.grade) : -1;
                                                    return treeSortGradeDir === 'asc' ? gA - gB : gB - gA;
                                                });
                                                const binsFiltered = binsSorted.filter(bg => {
                                                    const binName = getBinName(bg.binId);
                                                    const cablesForBin = [...(data?.cables ?? [])].filter(c => c.binId === bg.binId);
                                                    const hasMatchingCable = cablesForBin.some(cg => {
                                                        const cableLabel = getCableName(cg.binId, cg.cableId, cg.cableName);
                                                        const cableApiName = (cg.cableName ?? cg.CableName ?? '').toString();
                                                        const sensorsForCable = (data?.sensors ?? []).filter(s => s.binId === cg.binId && s.cableId === cg.cableId);
                                                        const hasMatchingSensor = sensorsForCable.some(s => {
                                                            const sensorLabel = getSensorLabel(s);
                                                            const sensorApiName = (s.sensorName ?? s.SensorName ?? '').toString();
                                                            return labelMatchesFilter(sensorLabel) || labelMatchesFilter(sensorApiName) || labelMatchesFilter(String(s.sensorId));
                                                        });
                                                        return labelMatchesFilter(cableLabel) || labelMatchesFilter(cableApiName) || labelMatchesFilter(String(cg.cableId)) || hasMatchingSensor;
                                                    });
                                                    return labelMatchesFilter(binName) || labelMatchesFilter(String(bg.binId)) || hasMatchingCable;
                                                });
                                                return binsFiltered.map((bg, bi) => {
                                                const binKey = `${dateKey}-bin-${String(bg.binId ?? '')}-${bi}`;
                                                const cablesForBin = [...(data?.cables ?? [])].filter(c => c.binId === bg.binId).sort((a, b) => {
                                                    if (treeSortInnerBy === 'name') {
                                                        const nA = (getCableName(a?.binId, a?.cableId, a?.cableName) ?? '').toLowerCase();
                                                        const nB = (getCableName(b?.binId, b?.cableId, b?.cableName) ?? '').toLowerCase();
                                                        const cmp = nA.localeCompare(nB, undefined, { sensitivity: 'base' });
                                                        return treeSortNameDir === 'asc' ? cmp : -cmp;
                                                    }
                                                    const gA = a?.grade != null && !isNaN(a.grade) ? Number(a.grade) : -1;
                                                    const gB = b?.grade != null && !isNaN(b.grade) ? Number(b.grade) : -1;
                                                    return treeSortGradeDir === 'asc' ? gA - gB : gB - gA;
                                                }).filter(cg => {
                                                    const cableLabel = getCableName(cg.binId, cg.cableId, cg.cableName);
                                                    const cableApiName = (cg.cableName ?? cg.CableName ?? '').toString();
                                                    const sensorsForCable = (data?.sensors ?? []).filter(s => s.binId === cg.binId && s.cableId === cg.cableId);
                                                    const hasMatchingSensor = sensorsForCable.some(s => {
                                                        const sensorLabel = getSensorLabel(s);
                                                        const sensorApiName = (s.sensorName ?? s.SensorName ?? '').toString();
                                                        return labelMatchesFilter(sensorLabel) || labelMatchesFilter(sensorApiName) || labelMatchesFilter(String(s.sensorId));
                                                    });
                                                    return labelMatchesFilter(cableLabel) || labelMatchesFilter(cableApiName) || labelMatchesFilter(String(cg.cableId)) || hasMatchingSensor;
                                                });
                                                return (
                                                    <MuiMaterials.TreeItem
                                                        key={binKey}
                                                        itemId={binKey}
                                                        label={
                                                            <MuiMaterials.Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, width: '100%', fontSize: '0.75rem' }}>
                                                                <span style={{ flex: '1 1 0', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>{getBinName(bg.binId)}</span>
                                                                <span style={{ flex: '0 0 auto', color: gradeColor(bg.grade), fontWeight: 600, minWidth: 32, textAlign: 'right' }}>{bg.grade != null && !isNaN(bg.grade) ? Math.round(bg.grade) : '-'}</span>
                                                            </MuiMaterials.Box>
                                                        }
                                                    >
                                                        {cablesForBin.map((cg, ci) => {
                                                            const cableKey = `${binKey}-cable-${String(cg.cableId ?? '')}-${ci}`;
                                                            const cableMatches = labelMatchesFilter(getCableName(cg.binId, cg.cableId, cg.cableName)) || labelMatchesFilter((cg.cableName ?? cg.CableName ?? '').toString());
                                                            const allSensorsForCable = [...(data?.sensors ?? [])].filter(s => s.binId === cg.binId && s.cableId === cg.cableId);
                                                            const sensorsForCable = (cableMatches ? allSensorsForCable : allSensorsForCable.filter(s => labelMatchesFilter(getSensorLabel(s)) || labelMatchesFilter((s.sensorName ?? s.SensorName ?? '').toString()) || labelMatchesFilter(String(s.sensorId))))
                                                                .sort((a, b) => {
                                                                    if (treeSortInnerBy === 'name') {
                                                                        const nA = (getSensorLabel(a) ?? '').toLowerCase();
                                                                        const nB = (getSensorLabel(b) ?? '').toLowerCase();
                                                                        const cmp = nA.localeCompare(nB, undefined, { sensitivity: 'base' });
                                                                        return treeSortNameDir === 'asc' ? cmp : -cmp;
                                                                    }
                                                                    const gA = a?.grade != null && !isNaN(a.grade) ? Number(a.grade) : -1;
                                                                    const gB = b?.grade != null && !isNaN(b.grade) ? Number(b.grade) : -1;
                                                                    return treeSortGradeDir === 'asc' ? gA - gB : gB - gA;
                                                                });
                                                            return (
                                                                <MuiMaterials.TreeItem
                                                                    key={cableKey}
                                                                    itemId={cableKey}
                                                                    label={
                                                                        <MuiMaterials.Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, width: '100%', fontSize: '0.75rem' }}>
                                                                            <span style={{ flex: '1 1 0', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>{getCableName(cg.binId, cg.cableId, cg.cableName)}</span>
                                                                            <span style={{ flex: '0 0 auto', color: gradeColor(cg.grade), fontWeight: 600, minWidth: 32, textAlign: 'right' }}>{cg.grade != null && !isNaN(cg.grade) ? Math.round(cg.grade) : '-'}</span>
                                                                        </MuiMaterials.Box>
                                                                    }
                                                                >
                                                                    {sensorsForCable.map((sg, si) => {
                                                                        const sensorKey = `${cableKey}-sensor-${String(sg.sensorId ?? '')}-${si}`;
                                                                        return (
                                                                            <MuiMaterials.TreeItem
                                                                                key={sensorKey}
                                                                                itemId={sensorKey}
                                                                                label={<SensorTreeRowLabel sg={sg} />}
                                                                            />
                                                                        );
                                                                    })}
                                                                </MuiMaterials.TreeItem>
                                                            );
                                                        })}
                                                    </MuiMaterials.TreeItem>
                                                );
                                            })
                                            })()
                                        )}
                                    </MuiMaterials.TreeItem>
                                );
                            })}
                        </MuiMaterials.TreeView>
                            </MuiMaterials.Box>
                        </MuiMaterials.Box>
                    )}
                </ModalContentTile>
            </ModalBody>
            )}
        </ModalChrome>
        );
    };

    return renderModal();
};

export default SiteGradesModal;
